import PictureImage from '../Common/PictureImage';
import { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import Canonical from '../../seo/Canonical';
import Footer from '../Layouts/Footer';
import RelatedLinks from '../Common/RelatedLinks';
import FAQSection from '../FAQSection';
import FbaPricingGuideSection from './FbaPricingGuideSection';
import FbaPrepCostBreakdown from './FbaPrepCostBreakdown';
import FbaAmazonCostComparison from './FbaAmazonCostComparison';
import FbaPrepPricingCta from './FbaPrepPricingCta';
import { FBA_PREP_PRICING_FAQS } from '../../data/fbaPrepPricingData';
import { FBA_PREP_CONTACT_PATH } from './fbaPrepCta';
import './layout.css';
import './Fbapreppricing.css';

const FBAPricing = lazy(() => import('./Fbapricing'));
const FBAPricingTiers = lazy(() => import('./FBAPricingTiers'));

const HERO_BG_SRC = '/amazonfba/fbaprepricing.webp';

const sectionReveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.06 * i, duration: 0.45, ease: [0.25, 0.1, 0.25, 1] as const },
  }),
};

export default function FBAPrepPricing() {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <div className="fba-prep-pricing-page fba-prep-pricing-page--v3">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <section className="fba-hero hero-section" aria-label="Amazon FBA prep pricing">
        <PictureImage
          className="fba-hero-bg"
          src={HERO_BG_SRC}
          alt="Amazon FBA prep pricing and fulfilment costs UK"
          width={1920}
          height={1280}
          decoding="async"
          isHero
        />
        <div className="fba-hero-overlay" aria-hidden />
        <div className="fba-hero-content">
          <div className="fba-hero-inner">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <span className="fba-hero-tag">FBA prep pricing UK</span>
            </motion.div>
            <motion.h1
              className="fba-hero-title"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.06, duration: 0.5 }}
            >
              Amazon FBA Prep Cost &amp; Pricing UK
            </motion.h1>
            <motion.p
              className="fba-hero-sub"
              initial={prefersReducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.18, duration: 0.45 }}
            >
              Simple, transparent pricing with volume-based discounts. Review our{' '}
              <Link to="/pricing-fulfilment-services" className="fba-inline-link fba-inline-link--on-dark">
                fulfilment pricing structure
              </Link>
              , then request your bespoke{' '}
              <Link to="/amazon-fba-prep-services" className="fba-inline-link fba-inline-link--on-dark">
                Amazon FBA prep services UK
              </Link>{' '}
              quote today — tailored to your products, volumes, and marketplace requirements. See also our{' '}
              <Link to="/blog/fba-prep-centre-uk-pricing-2026" className="fba-inline-link fba-inline-link--on-dark">
                prep centre UK pricing guide (2026)
              </Link>{' '}
              and whether you{' '}
              <Link
                to="/blog/do-i-need-a-prep-centre-best-fba-prep-centre-uk-reddit-guide"
                className="fba-inline-link fba-inline-link--on-dark"
              >
                need a prep centre
              </Link>
              .
            </motion.p>
            <motion.div
              className="fba-hero-cta-row"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.4 }}
            >
              <a href="#fba-pricing" className="fba-hero-primary-btn">
                Use cost calculator
              </a>
              <Link to={FBA_PREP_CONTACT_PATH} className="fba-hero-secondary-btn">
                Get custom quote
              </Link>
            </motion.div>
            <motion.div
              className="fba-hero-pills"
              initial={prefersReducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.36, duration: 0.4 }}
            >
              {['From £0.30/unit at volume', 'Instant cost breakdown', 'Compare vs Amazon FBA'].map((t) => (
                <span key={t} className="fba-hero-pill">
                  ✓ {t}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Suspense fallback={null}>
        <FBAPricing
          showQuickEstimate
          headingTitle="Calculate Your FBA Prep Costs"
          headingSubtitle={
            <>
              Input monthly volume, product type and weight band for an instant prep cost breakdown — compare tiers, read our{' '}
              <Link to="/blog/what-is-fba-prep" className="fba-inline-link">what is FBA prep</Link> overview, or request a formal quote.
            </>
          }
          primaryCtaLabel="Get custom quote from calculator →"
        />
      </Suspense>

      <div className="fba-prep-mid fba-prep-mid--v3">
        <FbaPrepCostBreakdown />

        <div className="fba-prep-cta-spacer">
          <FbaPrepPricingCta
            label="Compare costs"
            title="Not sure which tier fits your FBA costs?"
            subtitle={
              <>
                Send your ASIN list and monthly volume — we will model prep vs labelling vs{' '}
                <Link to="/amazon-fba-shipments" className="fba-inline-link">FBA shipment</Link> costs before you commit stock.
              </>
            }
            variant="orange"
          />
        </div>

        <div className="fba-pricing-container fba-pricing-container--v3">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.12 }}
            custom={0}
            variants={prefersReducedMotion ? undefined : sectionReveal}
          >
            <Suspense fallback={null}>
              <FBAPricingTiers primaryCtaLabel="Get custom quote for this tier →" />
            </Suspense>
          </motion.div>
        </div>

        <FbaAmazonCostComparison />

        <div className="fba-prep-cta-spacer">
          <FbaPrepPricingCta
            title="Ready to compare prep centre fees with a real quote?"
            subtitle={
              <>
                Our team models FBA costs including{' '}
                <Link to="/blog/amazon-poly-bag-requirements-uk" className="fba-inline-link">polybagging</Link>, inspection and FC shipping —{' '}
                <Link to="/contact-fulfillment-services" className="fba-inline-link">contact sales</Link> for a tailored rate card.
              </>
            }
          />
        </div>

        <motion.div
          className="fba-guide-section-outer"
          custom={1}
          variants={prefersReducedMotion ? undefined : sectionReveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
        >
          <FbaPricingGuideSection />
        </motion.div>
      </div>

      <FAQSection
        headingId="fba-prep-pricing-faq"
        className="faqs-section"
        title="FBA prep pricing FAQs"
        description={
          <>
            Common questions about prep centre fees, how we calculate costs and how we compare to Amazon FBA — including{' '}
            <Link to="/blog/amazon-ending-fba-prep-services-uk-2026" className="fba-inline-link">July 2026 Amazon prep changes</Link>.
          </>
        }
        faqs={FBA_PREP_PRICING_FAQS}
        schemaScriptId="fba-prep-pricing-faq-schema"
      />

      <motion.div
        className="fba-cta-v3-wrap"
        initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
        whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.5 }}
      >
        <div className="fba-cta-v3" id="contact">
          <div className="fba-cta-v3-deco" aria-hidden />
          <div className="fba-cta-v3-inner">
            <div className="fba-cta-v3-text">
              <p className="fba-cta-v3-label">Get started today</p>
              <h2 className="fba-cta-v3-title">
                Get a custom quote <em>today</em>
              </h2>
              <p className="fba-cta-v3-sub">
                Share your Amazon FBA prep volumes, product categories, and marketplaces. We will provide a bespoke quote for{' '}
                <Link to="/amazon-fba-prep-services" className="fba-inline-link">Amazon FBA prep services in the UK</Link> using transparent, volume-based pricing — whether you need{' '}
                <Link to="/amazon-fba-prep-wholesale" className="fba-inline-link">wholesale FBA prep</Link> or{' '}
                <Link to="/amazon-fba-prep-private-label" className="fba-inline-link">private label prep</Link>.
              </p>
            </div>
            <div className="fba-cta-v3-actions">
              <Link to={FBA_PREP_CONTACT_PATH} className="fba-cta-v3-btn">
                Request my free quote →
              </Link>
              <p className="fba-cta-v3-note">No commitment · Responds within 1 business day</p>
            </div>
          </div>
        </div>
      </motion.div>

      <RelatedLinks
        className="fba-related-v3"
        title="Compare services"
        serviceLinks={[
          { to: '/pricing-fulfilment-services', label: 'Fulfilment pricing UK (main page)' },
          { to: '/amazon-fba-prep-services', label: 'Amazon FBA prep services UK' },
          { to: '/blog/amazon-fba-prep-centre-uk-guide-2026', label: 'Amazon FBA prep centre UK guide (2026)' },
        ]}
        blogLink={{ to: '/blog/fba-prep-centre-uk-pricing-2026', label: 'Amazon prep centre UK pricing (2026)' }}
      />

      <Footer />
    </div>
  );
}
